import { isForbiddenError, useIpLookup } from '@ottd/api';
import type { IpAsn, IpPlayer } from '@ottd/api';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { BevelWindow } from '@/components/prototype/bevel-window';
import { PageHeader } from '@/components/prototype/page-header';
import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { QueryState } from '@/components/prototype/query-state';
import { RelativeTime } from '@/components/prototype/relative-time';
import { useAuth } from '@/auth/use-auth';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatRelativeTime } from '@/lib/format';
import { PlayerLink } from '@/pages/shared';

/** Each AS the IP routes through, flagged (with the in-game kick reason) when banned. */
function AsnRoutes({ asns }: { asns: IpAsn[] }) {
  const { t } = useTranslation('player');

  return (
    <div>
      <Label>{t('ipLookup.asns')}</Label>
      <ul className="mt-1 flex flex-col gap-1">
        {asns.map((asn) => (
          <li key={asn.asNumber} className="flex flex-wrap items-center gap-2 font-mono text-sm">
            <span className="text-strong">AS{asn.asNumber}</span>
            {asn.asName !== null ? <span className="text-dim">{asn.asName}</span> : null}
            {asn.ban !== null ? <Badge variant="orange">{t('ipLookup.asnBanned')}</Badge> : null}
            {asn.ban?.kickReason ? (
              <span className="text-faint text-xs">
                {t('ipLookup.asnBanKick', { reason: asn.ban.kickReason })}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A player seen on the IP (legacy `ip/show`): when they last used it - the
 * list is ordered by it, most recent first - and their played/won totals.
 */
function IpPlayerRow({ player }: { player: IpPlayer }) {
  const { t } = useTranslation('player');

  return (
    <li className="flex flex-wrap items-center gap-2">
      <PlayerAvatar name={player.name} size={22} />
      <PlayerLink name={player.name} className="text-strong font-sans" />
      {player.isBanned ? <Badge variant="orange">{t('ipLookup.banned')}</Badge> : null}
      {player.lastSeen ? (
        <RelativeTime
          iso={player.lastSeen}
          label={t('ipLookup.lastSeen', { ago: formatRelativeTime(player.lastSeen) })}
          className="text-dim font-mono text-[11px]"
        />
      ) : null}
      <span className="text-faint ml-auto font-mono text-[11px]">
        {t('ipLookup.playedWon', { played: player.gamesPlayed, won: player.gamesWon })}
      </span>
    </li>
  );
}

export default function IpLookupPage() {
  const { t } = useTranslation('player');
  const { status } = useAuth();
  // Legacy deep links: /ip/{ip} and /asn/{ip} open the lookup pre-filled.
  const { ip: ipParam } = useParams<{ ip?: string }>();
  const [input, setInput] = useState(ipParam ?? '');
  const [ip, setIp] = useState<string | undefined>(ipParam);

  const query = useIpLookup(ip, status === 'authenticated');
  const forbidden = isForbiddenError(query.error);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (trimmed.length > 0) setIp(trimmed);
  };

  return (
    <div className="mx-auto max-w-[720px] px-4 py-6 sm:px-5">
      <PageHeader
        eyebrow={t('ipLookup.eyebrow')}
        title={t('ipLookup.title')}
        subtitle={t('ipLookup.subtitle')}
      />

      {status !== 'authenticated' ? (
        <BevelWindow className="mt-8" title={t('ipLookup.signInTitle')}>
          <p className="text-dim font-mono text-sm">
            <Trans
              i18nKey="ipLookup.signInBody"
              ns="player"
              components={[<Link key="login" to="/login" className="text-orange" />]}
            />
          </p>
        </BevelWindow>
      ) : (
        <BevelWindow className="mt-8" title={t('ipLookup.lookupTitle')}>
          <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="ip-input">{t('ipLookup.ipLabel')}</Label>
              <Input
                id="ip-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('ipLookup.ipPlaceholder')}
                autoComplete="off"
                spellCheck={false}
              />
            </div>
            <Button type="submit" variant="primary" disabled={input.trim().length === 0}>
              {t('ipLookup.submit')}
            </Button>
          </form>

          {ip !== undefined ? (
            <div className="mt-4">
              <QueryState isLoading={query.isLoading} isError={query.isError} error={query.error}>
                {forbidden ? (
                  <p className="text-destructive font-mono text-sm">{t('ipLookup.forbidden')}</p>
                ) : query.data ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm text-strong">{query.data.ip}</span>
                      {query.data.isIpBanned ? (
                        <Badge variant="orange">{t('ipLookup.ipBanned')}</Badge>
                      ) : null}
                      {query.data.isAsnBanned ? (
                        <Badge variant="orange">{t('ipLookup.asnBanned')}</Badge>
                      ) : null}
                    </div>

                    <div>
                      <Label>{t('ipLookup.playersSeen')}</Label>
                      {query.data.players.length === 0 ? (
                        <p className="text-faint mt-1 font-mono text-xs">
                          {t('ipLookup.noPlayers')}
                        </p>
                      ) : (
                        <ul className="mt-1 flex flex-col gap-1">
                          {query.data.players.map((player) => (
                            <IpPlayerRow key={player.id} player={player} />
                          ))}
                        </ul>
                      )}
                    </div>

                    {query.data.asns.length > 0 ? <AsnRoutes asns={query.data.asns} /> : null}

                    <div>
                      <Label>{t('ipLookup.asnData')}</Label>
                      <pre className="bevel-in bg-bar-bg mt-1 max-h-64 overflow-auto p-3 font-mono text-[11px] text-text">
                        {query.data.asn
                          ? JSON.stringify(query.data.asn, null, 2)
                          : t('ipLookup.asnUnknown')}
                      </pre>
                    </div>
                  </div>
                ) : null}
              </QueryState>
            </div>
          ) : null}
        </BevelWindow>
      )}
    </div>
  );
}
